// POR QUE NO SE ENVIO ESE SMS, dicho en el idioma de quien lo mandó.
//
// Crismar Perez (agencia de Maricarmen Nieto, 2026-09-14): *"al enviar el
// mensaje para que el cliente responda SI nos sale error y no se envía"*, con
// una captura donde el aviso decía **"Enviados: 0 · fallaron: 1"** y nada más.
//
// El servidor SÍ dice por qué falla cada envío —no hay línea de SMS conectada,
// el número está en la lista de No-Textear, el cliente no tiene teléfono— y con
// un texto pensado para leerse. Lo que pasaba es que el envío EN LOTE lo tiraba
// a la basura (`catch{ failed++ }`) y enseñaba solo un número; y encima lo
// enseñaba en un aviso VERDE, de los de "todo bien", aunque no se hubiera
// enviado ni uno.
//
// Un contador sin motivo no se puede arreglar: la agencia no sabe si le falta
// conectar la línea, si ese cliente no tiene teléfono o si el número pidió que
// no le escribieran. Aquí se traduce el código del servidor a esa frase.
//
// Vive en su propio módulo (y no dentro de deskte-full.jsx) para que una prueba
// pueda IMPORTARLO y comprobar que los códigos son EXACTAMENTE los que la ruta
// devuelve — un `.jsx` no se puede importar desde una prueba (regla 48).

/**
 * Los códigos que devuelve `POST /renewal-reviews/:id/sms` cuando no envía, y
 * qué significan para quien está mirando la pantalla.
 *
 * Si la ruta añade uno nuevo y no se añade aquí, NO se pierde: se enseña el
 * texto que mandó el servidor (en inglés). Perder el motivo entero es lo que no
 * puede volver a pasar.
 */
export const MOTIVOS_DEL_SMS = {
  'no-phone': {
    es: 'el cliente no tiene teléfono en la ficha',
    en: 'the client has no phone number on file',
  },
  'no-integration': {
    es: 'no hay una línea de SMS conectada — conéctala en Mi Cuenta → SMS y Llamadas',
    en: 'no SMS line is connected — connect one in My Account → SMS & Calls',
  },
  suppressed: {
    es: 'ese número está en tu lista de No-Textear (contestó STOP o alguien lo agregó a mano)',
    en: "that number is on your Do-Not-Text list (it replied STOP or someone added it by hand)",
  },
  // Los del link por CORREO (`POST /renewal-reviews/:id/email`, 2026-10-05).
  'no-email': {
    es: 'el cliente no tiene correo en la ficha',
    en: 'the client has no email on file',
  },
  'no-mailbox': {
    es: 'no hay un correo conectado — conecta Gmail u Outlook en "Mi Correo"',
    en: 'no mailbox is connected — connect Gmail or Outlook under "My Email"',
  },
  'no-supervisor': {
    es: 'este asiento no tiene agente asignado',
    en: 'this seat has no supervising agent assigned',
  },
  'send-failed': {
    es: 'el correo no salió; inténtalo de nuevo',
    en: 'the email did not go out; try again',
  },
};

/**
 * El motivo en una frase. `codigo` es lo que vino en `error`; `mensaje`, el
 * texto que mandó el servidor (o el del error de red, si no llegó a contestar).
 */
export function motivoDelSms(codigo, mensaje, lang) {
  const es = lang === 'es';
  const m = MOTIVOS_DEL_SMS[String(codigo || '')];
  if (m) return es ? m.es : m.en;
  const texto = String(mensaje || '').trim();
  if (texto) return texto;
  return es ? 'error desconocido' : 'unknown error';
}

/**
 * El resumen de un envío en lote: cuántos salieron y, si alguno falló, POR QUÉ.
 *
 * `fallos` es una lista de `{ nombre, motivo }`. Los motivos se agrupan —diez
 * clientes con la misma línea sin conectar son un solo problema, no diez— y se
 * nombra al cliente cuando es uno solo, que es cuando el nombre ayuda.
 */
export function resumenDelEnvio({ enviados, sinTelefono, fallos, lang }) {
  const es = lang === 'es';
  const lista = Array.isArray(fallos) ? fallos : [];
  const partes = [`${es ? 'Enviados' : 'Sent'}: ${enviados}`];
  if (sinTelefono) partes.push(`${es ? 'sin tel.' : 'no phone'}: ${sinTelefono}`);
  if (lista.length) partes.push(`${es ? 'fallaron' : 'failed'}: ${lista.length}`);
  let texto = partes.join(' · ');

  if (lista.length) {
    const porMotivo = new Map();
    for (const f of lista) {
      const motivo = String(f?.motivo || '').trim() || (es ? 'error desconocido' : 'unknown error');
      if (!porMotivo.has(motivo)) porMotivo.set(motivo, []);
      porMotivo.get(motivo).push(String(f?.nombre || '').trim());
    }
    const frases = [];
    for (const [motivo, nombres] of porMotivo) {
      const conNombre = nombres.filter(Boolean);
      // El nombre solo cuando es UNO: con cinco, la lista tapa el motivo, que es
      // lo que hay que leer.
      frases.push(conNombre.length === 1 ? `${conNombre[0]}: ${motivo}` : motivo);
      if (frases.length >= 3) break;
    }
    texto += ` — ${frases.join(' · ')}`;
  }
  return texto;
}
